import React, { FC } from 'react'
import styles from 'styles/components/calendar.module.scss'
import format from 'date-fns/format'
import getDate from 'date-fns/getDate'
import isSameDay from 'date-fns/isSameDay'
import isSameMonth from 'date-fns/isSameMonth'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { setError } from 'reducks/modal/operations'
import { StoreCalendarType } from 'types/storeCalendar'
import { getStoreCurrentMonth } from 'reducks/storeCalendar/selectors'
import { dateToString } from 'lib/helper'

type Props = {
  date: Date
  handleStoreCalendars: (storeCalendar: StoreCalendarType) => void
  isRegularClosed: boolean
  isOrdered: boolean
  isSpecialHoliday: boolean
  isTemporaryClosed: boolean
  isTemporaryOpen: boolean
}

const CloseStoreCalendarCell: FC<Props> = React.memo((props) => {
  const {
    date,
    handleStoreCalendars,
    isRegularClosed,
    isOrdered,
    isSpecialHoliday,
    isTemporaryClosed,
    isTemporaryOpen,
  } = props

  const currentMonth = useSelector((state: State) => getStoreCurrentMonth(state))
  const isThisMonth = isSameMonth(date, currentMonth)

  const dispatch = useDispatch()

  const className = (() => {
    switch (true) {
      // 臨時営業日が祝日（通常祝日セル）
      case isThisMonth && isSpecialHoliday && isTemporaryOpen && !isTemporaryClosed:
        return styles.table__cell__holiday
      // 臨時営業日（通常セル）
      case isThisMonth && isTemporaryOpen && !isTemporaryClosed:
        return styles.table__cell
      // 臨時休業日（灰色セル）
      case isThisMonth && isTemporaryClosed && !isTemporaryOpen:
        return styles.table__cell_closed
      // 定休日が祝日（灰色セル）
      case isThisMonth && isSpecialHoliday && isRegularClosed:
        return styles.table__cell__holiday_disabled
      // 定休日（灰色セル）
      case isThisMonth && isRegularClosed:
        return styles.table__cell_disabled
      // 当日が祝日（黄色セル）
      case isThisMonth && isSpecialHoliday && isSameDay(date, new Date()):
        return styles.table__cell__holiday_today
      // 当日（黄色セル）
      case isThisMonth && isSameDay(date, new Date()):
        return styles.table__cell_today
      // 祝日
      case isThisMonth && isSpecialHoliday:
        return styles.table__cell__holiday
      default:
        return styles.table__cell
    }
  })()

  // タップ/クリックされたときの処理
  const onClick = () => {
    // 今月の日付のみタップ可能
    if (!isThisMonth) return null

    // オーダーが存在している日付を選択したらエラーを表示
    if (isOrdered) {
      return dispatch(
        setError(
          `選択した日付（${format(date, 'yyyy年M月d日')}）は、
              すでに注文が存在しているため、臨時休業日を設定することは出来ません`
        )
      )
    }

    // エラー文を初期化
    dispatch(setError(''))

    const value = {
      date: dateToString(date),
      status: '',
    }

    // 臨時休業日または臨時営業日を選択したらステータスをブランクにする（Firestore更新時に削除対象とする）
    if (isTemporaryClosed || isTemporaryOpen) {
      return handleStoreCalendars(value)
    }

    // 営業日を選択したら臨時休業日として追加
    if (!isRegularClosed) {
      value.status = 'close'
      return handleStoreCalendars(value)
    }
  }

  return (
    <td className={className} onClick={() => onClick()}>
      <span className={styles.table__cell__date}>
        {isThisMonth ? getDate(date) : ''}
        {isThisMonth && isOrdered && <span className={styles.table__mark} />}
      </span>
    </td>
  )
})

export default CloseStoreCalendarCell
